import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import ReactApexChart from "react-apexcharts";
import { useAuth } from "../../../../services/useAuth";
import { useTitle } from "../../../../hooks/useTitle";
import { withAuth } from "../../../../components/HOC/withAuth";
import AppPage from "../../../../components/CustomUI/AppPage";
import CardContainer from "../../../../components/CustomUI/CardContainer";
import { Table, Tr, Td } from "../../../../components/BaseUI/Table";
import { ToastContainer, Toast } from "../../../../components/CustomUI/Toast";
import Lozenge from "../../../../components/BaseUI/Lozenge";
import Button from "../../../../components/BaseUI/Button";
import { FiDownload } from "react-icons/fi";
import { useReportFilters } from "../shared/useReportFilters";
import ReportViewOptionsPanel from "../shared/ReportViewOptionsPanel";
import { useColumnVisibility } from "../../../../hooks/useColumnVisibility";
import { KpiCard, SectionSpinner, SectionEmpty, fmtDT, toISO, exportXLSX } from "../shared/utils";
import { API_URL, RP_CHART_MONO, RP_DONUT_TEALS } from "../shared/constants";
import "../Reports.scss";

const TYPE_TR = { start: "Başlatma", stop: "Durdurma", pause: "Duraklatma", resume: "Sürdürme", entry: "Giriş" };
const TYPE_APP = { start: "success-subtle", stop: "danger-subtle", pause: "warning-subtle", resume: "inprogress-subtle", entry: "default-subtle" };
const TYPE_COLORS = { start: "#22c55e", stop: "#e74c3c", pause: "#f97316", resume: "#3498db", entry: "#94a3b8" };

const ACTIVITY_TABLE_COLUMNS = ["Operatör", "Makine", "Aktivite", "Zaman", "Konum"].map(
  (name, id) => ({ name, id, sortable: false }),
);

const ActivitiesReport = () => {
  useTitle("Rovion MES - Operatör Aktiviteleri");
  const { getToken } = useAuth();
  const filters = useReportFilters();
  const {
    visibleColumns,
    toggleColumnVisibility,
    resetToDefault,
  } = useColumnVisibility("activities-report-list", ACTIVITY_TABLE_COLUMNS);

  const [machines, setMachines] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [actData, setActData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toasts, setToasts] = useState([]);

  const authH = useCallback(() => ({ headers: { Authorization: `Bearer ${getToken()}` } }), [getToken]);
  const addToast = useCallback((msg, type = "success") => setToasts((p) => [...p, { id: Date.now(), message: msg, type }]), []);
  const dismissToast = useCallback((id) => setToasts((p) => p.filter((t) => t.id !== id)), []);

  useEffect(() => {
    (async () => {
      try {
        const [mR, sR] = await Promise.all([
          axios.get(`${API_URL}/api/machines`, authH()),
          axios.get(`${API_URL}/api/shifts`, authH()),
        ]);
        setMachines(mR.data.machines || mR.data || []);
        setShifts(sR.data.shifts || []);
      } catch {}
    })();
  }, [authH]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      p.set("startDate", toISO(filters.startDate, false));
      p.set("endDate", toISO(filters.endDate, true));
      if (filters.machineIds.length === 1) p.set("machineId", filters.machineIds[0]);
      else if (filters.machineIds.length > 1) filters.machineIds.forEach((id) => p.append("machineId", id));
      p.set("limit", "1000");
      const r = await axios.get(`${API_URL}/api/activities?${p}`, authH());
      setActData(r.data.activities || r.data.data?.activities || r.data || []);
    } catch {
      addToast("Aktivite verisi yüklenemedi", "danger");
      setActData([]);
    } finally {
      setLoading(false);
    }
  }, [filters.startDate, filters.endDate, filters.machineIds, authH, addToast]);

  useEffect(() => { fetchData(); }, []); // eslint-disable-line

  const derived = useMemo(() => {
    const activities = Array.isArray(actData) ? actData : [];
    if (!activities.length) return null;

    const typeMap = {}, machMap = {}, opMap = {}, dayMap = {};
    activities.forEach((a) => {
      const t = a.activityType || "entry";
      typeMap[t] = (typeMap[t] || 0) + 1;

      const mn = a.machineId?.name || "Bilinmeyen";
      machMap[mn] = (machMap[mn] || 0) + 1;

      const opName = a.userId?.name || a.userId?.username || "Bilinmeyen";
      opMap[opName] = (opMap[opName] || 0) + 1;

      const day = new Date(a.timestamp).toISOString().slice(0, 10);
      dayMap[day] = (dayMap[day] || 0) + 1;
    });

    // Hourly distribution (0-23)
    const hourMap = Array(24).fill(0);
    activities.forEach((a) => {
      const h = new Date(a.timestamp).getHours();
      hourMap[h]++;
    });

    const uniqueOps = new Set(activities.map((a) => String(a.userId?._id || a.userId))).size;
    const mostActiveType = Object.entries(typeMap).sort((a, b) => b[1] - a[1])[0]?.[0] || null;

    // Operator ranking (top 10)
    const opRanking = Object.entries(opMap)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([name, count]) => ({ name, count }));

    // Daily trend
    const dayKeys = Object.keys(dayMap).sort();

    // Machine sorted
    const machArr = Object.entries(machMap).sort((a, b) => b[1] - a[1]).slice(0, 10);

    return { activities, typeMap, machArr, opMap, opRanking, hourMap, dayMap, dayKeys, uniqueOps, mostActiveType };
  }, [actData]);

  const exportActs = useCallback(() => {
    const activities = Array.isArray(actData) ? actData : [];
    exportXLSX(
      activities.map((a) => ({
        Operatör: a.userId?.name || a.userId?.username || "—",
        Makine: a.machineId?.name || "—",
        Aktivite: TYPE_TR[a.activityType] || a.activityType || "—",
        Zaman: fmtDT(a.timestamp),
        Konum: a.location || "—",
        Notlar: a.notes || "—",
      })),
      `operatör-aktiviteleri-${filters.startDate}-${filters.endDate}`,
      "Operatör Aktiviteleri",
    );
  }, [actData, filters]);

  const renderContent = () => {
    if (loading) return <SectionSpinner />;
    if (!derived) return <SectionEmpty />;

    const { activities, typeMap, machArr, opRanking, hourMap, dayMap, dayKeys, uniqueOps, mostActiveType } = derived;

    return (
      <div className="d-flex flex-column gap-3">
        {/* KPI cards */}
        <div className="rp-grid-4">
          <KpiCard label="Toplam Aktivite" value={activities.length.toLocaleString("tr-TR")} />
          <KpiCard label="Benzersiz Operatör" value={uniqueOps} />
          <KpiCard label="Aktif Makine" value={machArr.length} />
          <KpiCard label="En Sık Tip" value={TYPE_TR[mostActiveType] || "—"} sub={mostActiveType ? <Lozenge appearance={TYPE_APP[mostActiveType] || "default-subtle"} isBold>{typeMap[mostActiveType]}</Lozenge> : null} />
        </div>

        <div className="d-flex justify-content-end">
          <Button appearance="subtle" onClick={exportActs} iconBefore={<FiDownload size={13} />}>Excel İndir</Button>
        </div>

        {/* Daily trend */}
        {dayKeys.length > 1 && (
          <CardContainer title="Günlük Aktivite Trendi">
            <ReactApexChart
              type="area"
              series={[{ name: "Aktivite Sayısı", data: dayKeys.map((d) => dayMap[d] || 0) }]}
              options={{
                chart: { type: "area", toolbar: { show: false }, fontFamily: "inherit", foreColor: "#334155" },
                colors: [RP_CHART_MONO],
                fill: { type: "gradient", gradient: { opacityFrom: 0.4, opacityTo: 0.05 } },
                stroke: { width: 2, curve: "smooth" },
                xaxis: { categories: dayKeys, labels: { rotate: -45, style: { fontSize: "10px" } } },
                dataLabels: { enabled: false },
                grid: { borderColor: "#f0f2f5" },
                tooltip: { y: { formatter: (v) => `${v} aktivite` } },
              }}
              height={220}
            />
          </CardContainer>
        )}

        {/* Hourly distribution + Activity type donut */}
        <div className="rp-grid-2">
          <CardContainer title="Saatlik Aktivite Dağılımı (00-23)">
            <ReactApexChart
              type="bar"
              series={[{ name: "Aktivite", data: hourMap }]}
              options={{
                chart: { type: "bar", toolbar: { show: false }, fontFamily: "inherit", foreColor: "#334155" },
                colors: [RP_CHART_MONO],
                plotOptions: { bar: { borderRadius: 3, columnWidth: "75%" } },
                xaxis: {
                  categories: Array.from({ length: 24 }, (_, i) => `${String(i).padStart(2, "0")}:00`),
                  labels: { rotate: -45, style: { fontSize: "9px" } },
                },
                dataLabels: { enabled: false },
                grid: { borderColor: "#f0f2f5" },
                tooltip: { y: { formatter: (v) => `${v} aktivite` } },
              }}
              height={220}
            />
          </CardContainer>
          <CardContainer title="Aktivite Tipi Dağılımı">
            <ReactApexChart
              type="donut"
              series={Object.values(typeMap)}
              options={{
                chart: { type: "donut" },
                labels: Object.keys(typeMap).map((t) => TYPE_TR[t] || t),
                colors: Object.keys(typeMap).map((t) => TYPE_COLORS[t] || "#94a3b8"),
                legend: { position: "bottom" },
                dataLabels: { formatter: (v) => `${Math.round(v)}%` },
              }}
              height={250}
            />
          </CardContainer>
        </div>

        {/* Operator ranking + Machine activity */}
        <div className="rp-grid-2">
          <CardContainer title="Operatör Sıralaması (En Aktif 10)">
            <ReactApexChart
              type="bar"
              series={[{ name: "Aktivite", data: opRanking.map((o) => o.count) }]}
              options={{
                chart: { type: "bar", toolbar: { show: false } },
                plotOptions: { bar: { horizontal: true, borderRadius: 4, distributed: false } },
                colors: ["#8b5cf6"],
                xaxis: { categories: opRanking.map((o) => o.name) },
                dataLabels: { enabled: true },
                grid: { borderColor: "#f0f2f5" },
              }}
              height={Math.max(200, opRanking.length * 42)}
            />
          </CardContainer>
          <CardContainer title="Makine Başına Aktivite Sayısı">
            <ReactApexChart
              type="bar"
              series={[{ name: "Aktivite", data: machArr.map(([, v]) => v) }]}
              options={{
                chart: { type: "bar", toolbar: { show: false } },
                plotOptions: { bar: { horizontal: true, borderRadius: 4 } },
                colors: [RP_CHART_MONO],
                xaxis: { categories: machArr.map(([name]) => name) },
                dataLabels: { enabled: true },
                grid: { borderColor: "#f0f2f5" },
              }}
              height={Math.max(200, machArr.length * 42)}
            />
          </CardContainer>
        </div>

        {/* Detail table */}
        <CardContainer title="Aktivite Kayıtları Detayı">
          <Table
            columns={ACTIVITY_TABLE_COLUMNS}
            visibleColumns={visibleColumns}
            search={true}
            defaultPageSize={25}
          >
            {activities.map((a, i) => (
              <Tr key={i} data={a}>
                {visibleColumns.includes(0) && (
                  <Td><strong>{a.userId?.name || a.userId?.username || <span className="text-muted">—</span>}</strong></Td>
                )}
                {visibleColumns.includes(1) && (
                  <Td>{a.machineId?.name || <span className="text-muted">—</span>}</Td>
                )}
                {visibleColumns.includes(2) && (
                  <Td><Lozenge appearance={TYPE_APP[a.activityType] || "default-subtle"} isBold>{TYPE_TR[a.activityType] || a.activityType || "—"}</Lozenge></Td>
                )}
                {visibleColumns.includes(3) && (
                  <Td>{fmtDT(a.timestamp)}</Td>
                )}
                {visibleColumns.includes(4) && (
                  <Td><span className="text-muted small">{a.location || "—"}</span></Td>
                )}
              </Tr>
            ))}
          </Table>
        </CardContainer>
      </div>
    );
  };

  return (
    <AppPage
      title="Operatör Aktiviteleri"
      actions={
        <ReportViewOptionsPanel
          filters={filters}
          machines={machines}
          shifts={shifts}
          onApply={fetchData}
          columns={ACTIVITY_TABLE_COLUMNS}
          visibleColumns={visibleColumns}
          onToggleColumn={toggleColumnVisibility}
          onResetColumns={resetToDefault}
        />
      }
    >
      <ToastContainer>
        {toasts.map((t) => (
          <Toast key={t.id} title={t.message} appearance={t.type === "danger" ? "danger" : "success"} onClose={() => dismissToast(t.id)} />
        ))}
      </ToastContainer>
      {renderContent()}
    </AppPage>
  );
};

export default withAuth(ActivitiesReport);
